import { createClient } from '@supabase/supabase-js'

// Replace these with your actual Supabase project credentials
export const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://dtskgpqvvxcbtcsmedrq.supabase.co'
export const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR0c2tncHF2dnhjYnRjc21lZHJxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ5ODg3NTksImV4cCI6MjA3MDU2NDc1OX0.XYMVuCHm6Kit_bg9j44nGbiyG1_ejZeF2A3RbqmxpTU'

// Create a single supabase client for interacting with your database
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Helper functions for common database operations
export const supabaseHelpers = {
  // Get all posts with user information
  async getPosts(filters = {}) {
    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        users:posted_by_id (
          id,
          username,
          profile_image,
          display_name,
          is_organization
        )
      `)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  },

  // Get a single post with user information
  async getPost(postId) {
    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        users:posted_by_id (
          id,
          username,
          profile_image,
          display_name,
          is_organization
        )
      `)
      .eq('id', postId)
      .eq('is_deleted', false)
      .single()

    if (error) throw error
    return data
  },

  // Get user profile
  async getUserProfile(userId) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) throw error
    return data
  },

  // Get user posts
  async getUserPosts(userId, filters = {}) {
    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .eq('posted_by_id', userId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  },

  // Create a new post
  async createPost(postData) {
    const { data, error } = await supabase
      .from('posts')
      .insert([postData])
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Update a post
  async updatePost(postId, updates) {
    const { data, error } = await supabase
      .from('posts')
      .update(updates)
      .eq('id', postId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Delete a post (soft delete)
  async deletePost(postId) {
    const { data, error } = await supabase
      .from('posts')
      .update({ is_deleted: true })
      .eq('id', postId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Follow a user
  async followUser(followerId, followingId) {
    const { data, error } = await supabase
      .from('follows')
      .insert([
        { follower_id: followerId, following_id: followingId }
      ])
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Unfollow a user
  async unfollowUser(followerId, followingId) {
    const { data, error } = await supabase
      .from('follows')
      .delete()
      .eq('follower_id', followerId)
      .eq('following_id', followingId)

    if (error) throw error
    return data
  },

  // Check if user is following another user
  async isFollowing(followerId, followingId) {
    const { data, error } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', followerId)
      .eq('following_id', followingId)
      .single()

    return !error && data !== null
  },

  // Get followers
  async getFollowers(userId) {
    const { data, error } = await supabase
      .from('follows')
      .select(`
        follower_id,
        users:follower_id (
          id,
          username,
          profile_image,
          display_name
        )
      `)
      .eq('following_id', userId)

    if (error) throw error
    return data.map(follow => follow.users)
  },

  // Get following
  async getFollowing(userId) {
    const { data, error } = await supabase
      .from('follows')
      .select(`
        following_id,
        users:following_id (
          id,
          username,
          profile_image,
          display_name
        )
      `)
      .eq('follower_id', userId)

    if (error) throw error
    return data.map(follow => follow.users)
  },

  // Save a post
  async savePost(userId, postId) {
    const { data, error } = await supabase
      .from('saved_posts')
      .insert([{ user_id: userId, post_id: postId }])
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Unsave a post
  async unsavePost(userId, postId) {
    const { data, error } = await supabase
      .from('saved_posts')
      .delete()
      .eq('user_id', userId)
      .eq('post_id', postId)

    if (error) throw error
    return data
  },

  // Get saved posts
  async getSavedPosts(userId) {
    const { data, error } = await supabase
      .from('saved_posts')
      .select(`
        *,
        posts:post_id (
          *,
          users:posted_by_id (
            id,
            username,
            profile_image,
            display_name
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data.map(save => save.posts)
  },

  // Like a post
  async likePost(userId, postId) {
    const { data, error } = await supabase
      .from('post_likes')
      .insert([{ user_id: userId, post_id: postId }])
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Unlike a post
  async unlikePost(userId, postId) {
    const { data, error } = await supabase
      .from('post_likes')
      .delete()
      .eq('user_id', userId)
      .eq('post_id', postId)

    if (error) throw error
    return data
  },

  // Check if user liked a post
  async isLiked(userId, postId) {
    const { data, error } = await supabase
      .from('post_likes')
      .select('id')
      .eq('user_id', userId)
      .eq('post_id', postId)
      .single()

    return !error && data !== null
  },

  // Get post likes count
  async getPostLikesCount(postId) {
    const { count, error } = await supabase
      .from('post_likes')
      .select('*', { count: 'exact', head: true })
      .eq('post_id', postId)

    if (error) throw error
    return count || 0
  },

  // Search users
  async searchUsers(query) {
    const { data, error } = await supabase
      .from('users')
      .select('id, username, profile_image, display_name')
      .ilike('username', `%${query}%`)
      .limit(10)

    if (error) throw error
    return data
  },

  // Update user profile
  async updateUserProfile(userId, updates) {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Wallet transfer using edge function
  async walletTransfer({ fromUserId, toUserId, amount, feePayer, description }) {
    try {
      const session = await supabase.auth.getSession()
      const token = session.data.session?.access_token

      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/wallet-transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fromUserId,
          toUserId,
          field: 'winicoins',
          amount,
          deductFeeFrom: feePayer === 'sender' ? 'sender' : 'receiver',
          description: description || 'Transfer'
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Transfer failed with status ${response.status}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Transfer failed')
      }

      return data
    } catch (error) {
      console.error('[walletTransfer] Error:', error)
      throw error
    }
  }
}

export default supabase
